import { faUsers } from '@fortawesome/pro-solid-svg-icons/faUsers';
import useNonInitialEffect from '@versiondos/hooks';
import { fetchStaffThunk } from 'actions/staff/StaffActions';
import { Input } from 'components/UI/V3/Forms/Input/Input';
import TagsSelector from 'components/UI/V3/TagsSelector';
import isEmpty from 'lodash/isEmpty';
import Staff from 'model/Staff';
import { ChangeEvent, FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { StaffStatus } from 'reducers/staff/StaffState';
import { RootState } from 'store';
import { Room, RoomPhoto } from '@spike/room-model';
import PhotosSelector from 'components/UI/V3/PhotosSelector';
import styles from './RoomDetails.module.scss';
import { Button } from './Button';
import { Textarea } from 'components/UI/V3/Forms/Textarea/Textarea';
import Select, { SelectOption } from 'components/UI/V3/Forms/Select';
import { useMasterData } from 'hooks';
import { Option } from '@spike/model';

interface RoomDetailsProps {
    room: Room;
    nameError: string | null;
    className?: string;
    onChange?: (room: Room) => void;
    onNext?: () => void;
}

export const RoomDetails: FunctionComponent<RoomDetailsProps> = props => {
    const dispatch = useDispatch();

    const staffMembers = useSelector<RootState, Array<Staff>>(state => state.staff.staff);
    const staffStatus = useSelector<RootState, StaffStatus>(state => state.staff.status);
    const masterData = useMasterData();

    const [loading, setLoading] = useState({ staff: true });

    useEffect(() => {
        if (staffMembers.length === 0) {
            dispatch(fetchStaffThunk());
        } else {
            setLoading(prev => ({ ...prev, staff: false }));
        }
    }, []);

    useNonInitialEffect(() => {
        switch (staffStatus) {
            case StaffStatus.FetchSuccess:
                setLoading(prev => ({ ...prev, staff: false }));
                break;
            case StaffStatus.Error:
                setLoading(prev => ({ ...prev, staff: false }));
                break;
        }
    }, [staffStatus]);

    const disableNext = useMemo(() => isEmpty(props.room.name) || props.room.roomType === null, [props.room]);

    const roomTypeOptions = useMemo(
        () =>
            masterData.roomTypes.map(roomType => ({
                value: roomType.id.toString(),
                label: roomType.name,
                data: roomType
            })),
        [masterData]
    );

    const changeNameHandler = (event: ChangeEvent<HTMLInputElement>) => {
        props.onChange?.({ ...props.room, name: event.target.value });
    };

    const changeRoomTypeHandler = (value: string, option: SelectOption<Option<number>>) => {
        props.onChange?.({ ...props.room, roomType: option.data ?? null });
    };

    const changeDescriptionHandler = (event: ChangeEvent<HTMLTextAreaElement>) => {
        props.onChange?.({ ...props.room, description: event.target.value });
    };

    const selectStaffHandler = (selectedStaffId: number) => {
        props.onChange?.({
            ...props.room,
            staffIds: [...props.room.staffIds.filter(staffId => staffId !== selectedStaffId), selectedStaffId]
        });
    };

    const selectAllStaffHandler = (staffIds: Array<number>) => {
        props.onChange?.({ ...props.room, staffIds: [...staffIds] });
    };

    const removeStaffHandler = (removedStaffId: number) => {
        props.onChange?.({
            ...props.room,
            staffIds: props.room.staffIds.filter(staffId => staffId !== removedStaffId)
        });
    };

    const tagOptions = useMemo(
        () =>
            staffMembers
                .filter(member => member.active && !member.deleted)
                .map(member => ({
                    id: member.id ?? 0,
                    name: `${member.person.firstName} ${member.person.lastName}`,
                    image: member.person.avatar
                })),
        [staffMembers]
    );

    const changePhotosHandler = (photos: Array<RoomPhoto>) => {
        props.onChange?.({ ...props.room, photos });
    };

    return (
        <div className={styles.container}>
            <div className={styles.row}>
                <Input
                    label="Room Name"
                    required={true}
                    value={props.room.name}
                    error={props.nameError}
                    onChange={changeNameHandler}
                />
                <Select
                    label="Room Type"
                    required={true}
                    options={roomTypeOptions}
                    value={props.room.roomType?.id.toString()}
                    placeholder="Select Room Type"
                    onChange={changeRoomTypeHandler}
                />
            </div>
            <Textarea
                label="Write a Short Description"
                value={props.room.description}
                onChange={changeDescriptionHandler}
                maxLength={400}
            />
            <TagsSelector
                title="Assign Staff Members"
                tags={tagOptions}
                selected={staffMembers
                    .filter(member => props.room.staffIds.includes(member.id ?? 0))
                    .map(member => ({
                        id: member.id ?? 0,
                        name: `${member.person.firstName} ${member.person.lastName}`,
                        image: member.person.avatar
                    }))}
                selectAllLabel="All Members"
                selectAllIcon={faUsers}
                loading={loading.staff}
                onSelect={selectStaffHandler}
                onSelectAll={selectAllStaffHandler}
                onRemove={removeStaffHandler}
            />
            <PhotosSelector
                title="Photos"
                photos={props.room.photos}
                showCoverTag={true}
                maxPhotos={10}
                multipleSelection={true}
                onChange={changePhotosHandler}
            />
            <Button
                label="Next"
                disabled={disableNext}
                onClick={props.onNext}
            />
        </div>
    );
};

export default RoomDetails;
